//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { CompanyFileSet } from 'src/app/core/interfaces/general/company-file';
import { DownloadableFileSet } from 'src/app/core/interfaces/general/downloadable-file';
import { EncryptionService } from './encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from './exceptions-service';
import { HttpClientService } from './http-client-service';
import { Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class FileService {
  
  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  public base64ToFile(base64: string, fileName: string, contentType: string): File | null {
    try {
      const byteString=atob(base64);
      const byteArray =new Uint8Array(byteString.length);
    
      for (let i=0; i<byteString.length; i++) {
        byteArray[i]=byteString.charCodeAt(i);
      }
    
      return new File([byteArray], fileName, { type: contentType });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  //#region Company Files
  
  public getFileByCompanyIdAndCompanyFileTypeId(companyId: number, companyFileTypeId: number): Observable<CompanyFileSet> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi +
                                                                  eSimApiPaths.CompanyFiles + "/" +
                                                                  eSimApiPaths.CompanyFilesGetFileByCompanyIdAndCompanyFileTypeId +
                                                                  "?companyIdEncSS=" + companyIdEncSS +
                                                                  "&companyFileTypeId=" + companyFileTypeId, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getCompanyFileById(id: number): Observable<CompanyFileSet> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi +
                                                                  eSimApiPaths.CompanyFiles + "/" +
                                                                  eSimApiPaths.CompanyFilesGetFileById +
                                                                  "?idEncSS=" + idEncSS, undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public uploadCompanyFileData(formData: FormData): Observable<CompanyFileSet> {
    try {
      return this.httpClientService.post(environment.eSimApi +
                                         eSimApiPaths.CompanyFiles + "/" +
                                         eSimApiPaths.CompanyFilesUpload,
                                         formData
                                        );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#endregion
  
  //#region DownloadableFiles

  public downloadDownloadableFiles(userId: number): Observable<DownloadableFileSet[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<DownloadableFileSet[]>(environment.eSimApi +
                                                                                     eSimApiPaths.DownloadableFiles +
                                                                                     "?userIdEncSS=" + userIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getDownloadableFileById(id: number): Observable<DownloadableFileSet> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi +
                                                                  eSimApiPaths.DownloadableFiles + "/" +
                                                                  eSimApiPaths.DownloadableFilesGetFileById +
                                                                  "?idEncSS=" + idEncSS, undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public putDownloadableFile(downloadableFileSet: DownloadableFileSet): Observable<DownloadableFileSet> {
    try {
      //JSON.stringify(user)
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(downloadableFileSet), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const fileEncSS=encResult.result;
                            
                            return this.httpClientService.put(environment.eSimApi +
                                                              eSimApiPaths.DownloadableFiles +
                                                              "?id=" + downloadableFileSet.id +
                                                              "&fileEncSS=" + fileEncSS, undefined, {});
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public uploadDownloadableFileData(formData: FormData): Observable<DownloadableFileSet> {
    try {
      return this.httpClientService.post(environment.eSimApi +
                                         eSimApiPaths.DownloadableFiles + "/" +
                                         eSimApiPaths.DownloadableFilesUpload,
                                         formData
                                        );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#endregion

  //#region Intune
    
  public uploadIntuneImportDeviceCsv(companyId: number, formData: FormData, fileCreatedDate?: string): Observable<void> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.MdmIntune + "/" +
                                                               eSimApiPaths.MdmIntuneImportDeviceCsv +
                                                               "?companyIdEncSS=" + companyIdEncSS +
                                                               (fileCreatedDate ? "&fileCreatedDate=" + fileCreatedDate : ""),
                                                               formData
                                                              );
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public uploadIntuneImportUserCsv(companyId: number, formData: FormData, fileCreatedDate?: string): Observable<void> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.MdmIntune + "/" +
                                                               eSimApiPaths.MdmIntuneImportUserCsv +
                                                               "?companyIdEncSS=" + companyIdEncSS +
                                                               (fileCreatedDate ? "&fileCreatedDate=" + fileCreatedDate : ""),
                                                               formData
                                                              );
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#endregion
}
